// ExternalAssignment.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";

const TENANT_HEADER = "x-tenant-id";

const ExternalAssignment = ({ resourceId, lineItemUrl, backEndUrl, platformUrl, tenantId: tenantIdProp }) => {
    const location = useLocation();
    const queryParams = useMemo(() => new URLSearchParams(location.search), [location.search]);

    const token = queryParams.get("token");

    // Accept either ?courseId (PK like _123_1) or ?courseUuid; fallback to localStorage
    const courseIdQuery = queryParams.get("courseId") || queryParams.get("courseUuid") || "";
    const courseIdStored = (() => {
        try { return localStorage.getItem("courseId") || ""; } catch { return ""; }
    })();
    const courseIdParam = courseIdQuery || courseIdStored;

    const userIdParam = queryParams.get("uuid") || ""; // can be PK (_nnn_1) or uuid

    // Tenant: prop -> ?tenantId/tenant -> localStorage('tenantId')
    const tenantFromQs = queryParams.get("tenantId") || queryParams.get("tenant") || "";
    const tenantFromLs = (() => { try { return localStorage.getItem("tenantId") || ""; } catch { return ""; } })();
    const tenantId = (tenantIdProp || tenantFromQs || tenantFromLs || "").trim();

    const [selfUuid, setSelfUuid] = useState("");
    const [enrollmentRole, setEnrollmentRole] = useState("");
    const [loadingEnrollment, setLoadingEnrollment] = useState(false);
    const [enrollmentError, setEnrollmentError] = useState("");

    // ---- Completed Date (student UI; backend still expects "plannedDate") ----
    const [completedDate, setCompletedDate] = useState("");
    const [savingDate, setSavingDate] = useState(false);
    const [dateMessage, setDateMessage] = useState("");

    // Instructor view
    const [students, setStudents] = useState([]); // [{ uuid, idPk, name }]
    const [loadingStudents, setLoadingStudents] = useState(false);
    const [studentsError, setStudentsError] = useState("");
    const [grades, setGrades] = useState({}); // keyed by uuid
    const [existingScores, setExistingScores] = useState({}); // { uuid: number }
    const [rowStatuses, setRowStatuses] = useState({});
    const [posting, setPosting] = useState(false);
    const [error, setError] = useState("");
    const [completedDatesByUser, setCompletedDatesByUser] = useState({}); // { uuid: "YYYY-MM-DD" }

    // NEW: overall post notice banner
    const [postNotice, setPostNotice] = useState(null); // { type: 'success'|'warning'|'error', text: string }

    // Gradebook column discovery (from content)
    const [columnId, setColumnId] = useState("");
    const [loadingColumn, setLoadingColumn] = useState(false);
    const [columnError, setColumnError] = useState("");

    /** -------- Helpers -------- */
    const trimBase = (s = "") => s.replace(/\/+$/, "");
    const apiBase = () => trimBase(platformUrl || "");
    const isPk1 = (id = "") => id.startsWith("_");
    const coursePath = (id = "") => (isPk1(id) ? `courses/${id}` : `courses/uuid:${id}`);
    const userPath = (id = "") => (isPk1(id) ? `users/${id}` : `users/uuid:${id}`);
    const authHeader = token ? { Authorization: `Bearer ${token}` } : {};
    const tenantHeaders = (base = {}) => (tenantId ? { ...base, [TENANT_HEADER]: tenantId } : base);

    const userCacheRef = useRef(new Map());
    const getCachedUser = (key) => userCacheRef.current.get(key);
    const setCachedUser = (key, val) => userCacheRef.current.set(key, val);

    const fetchUserCanonical = async (idLike) => {
        if (!apiBase() || !token || !idLike) return { uuid: "", name: "" };
        const cacheKey = String(idLike).replace(/^uuid:/i, "");
        const cached = getCachedUser(cacheKey);
        if (cached) return cached;

        const url = `${apiBase()}/learn/api/public/v1/${userPath(idLike)}`;
        const res = await axios.get(url, { headers: authHeader });
        const data = res?.data || {};
        const uuid = (data.uuid || "").toString();
        const name =
            data?.name?.displayName ||
            [data?.name?.given, data?.name?.family].filter(Boolean).join(" ") ||
            data?.userName ||
            uuid;

        const out = { uuid, name };
        setCachedUser(cacheKey, out);
        return out;
    };

    const resolveSelfUuid = async () => {
        if (!userIdParam) return "";
        const { uuid } = await fetchUserCanonical(userIdParam);
        return uuid || userIdParam;
    };

    /** -------- Load self uuid once -------- */
    useEffect(() => {
        (async () => {
            try {
                const uuid = await resolveSelfUuid();
                setSelfUuid(uuid);
            } catch { /* noop */ }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [platformUrl, userIdParam, token]);

    /** -------- Enrollment (membership) -------- */
    useEffect(() => {
        const fetchEnrollment = async () => {
            if (!apiBase() || !courseIdParam || !userIdParam || !token) return;
            try {
                setLoadingEnrollment(true);
                setEnrollmentError("");
                const url = `${apiBase()}/learn/api/public/v1/${coursePath(courseIdParam)}/${userPath(userIdParam)}`;
                const res = await axios.get(url, { headers: authHeader });
                const role = res?.data?.courseRoleId || res?.data?.courseRole || "";
                setEnrollmentRole(role || "");
            } catch (e) {
                setEnrollmentError(e?.response?.data?.message || e?.message || "Failed to load enrollment.");
            } finally {
                setLoadingEnrollment(false);
            }
        };
        fetchEnrollment();
    }, [platformUrl, courseIdParam, userIdParam, token]);

    const isStudent = (enrollmentRole || "").toLowerCase() === "student";
    const instructorLike = new Set(["instructor", "teachingassistant", "grader", "coursebuilder"]);
    const isInstructor = instructorLike.has((enrollmentRole || "").toLowerCase());

    /** -------- Student: load existing completed date -------- */
    useEffect(() => {
        const loadOwnDate = async () => {
            if (!backEndUrl || !courseIdParam || !resourceId || !selfUuid) return;
            if (!isStudent) return;
            try {
                const url = `${trimBase(backEndUrl)}/external-assignments?courseId=${encodeURIComponent(
                    courseIdParam
                )}&resourceId=${encodeURIComponent(resourceId)}&userId=${encodeURIComponent(selfUuid)}`;
                const res = await axios.get(url, { headers: tenantHeaders() });
                const cd = res?.data?.PlannedDate || res?.data?.plannedDate || "";
                if (cd) setCompletedDate(cd);
            } catch {
                // ok if missing
            }
        };
        loadOwnDate();
    }, [backEndUrl, courseIdParam, resourceId, selfUuid, isStudent, tenantId]);

    /** -------- Instructors: roster (capture both PK and UUID) -------- */
    useEffect(() => {
        const fetchAllStudents = async () => {
            if (!apiBase() || !courseIdParam || !token) return;
            if (!isInstructor) return;
            try {
                setLoadingStudents(true);
                setStudentsError("");
                setStudents([]);

                const base = `${apiBase()}/learn/api/public/v1/${coursePath(courseIdParam)}/users`;
                let url = `${base}?limit=200&expand=user`;
                const byUuid = new Map();

                while (url) {
                    const res = await axios.get(url, { headers: authHeader });
                    const results = res?.data?.results ?? [];

                    for (const m of results) {
                        const mRole = String(m?.courseRoleId || m?.courseRole || "").toLowerCase();
                        if (mRole !== "student") continue;

                        const idPk = m?.user?.id || "";      // Blackboard PK (_nnn_1)
                        const uuid = m?.user?.uuid || "";    // UUID
                        const displayName =
                            m?.user?.name?.displayName ||
                            [m?.user?.name?.given, m?.user?.name?.family].filter(Boolean).join(" ") ||
                            m?.user?.userName ||
                            uuid ||
                            idPk;

                        if (uuid || idPk) byUuid.set(uuid || idPk, { uuid, idPk, name: displayName });
                    }

                    const next = res?.data?.paging?.nextPage;
                    url = next ? `${apiBase()}${next.startsWith("/") ? "" : "/"}${next}` : "";
                }

                const unique = Array.from(byUuid.values()).sort((a, b) => a.name.localeCompare(b.name));
                setStudents(unique);
            } catch (e) {
                setStudentsError(e?.response?.data?.message || e?.message || "Failed to load enrollments.");
            } finally {
                setLoadingStudents(false);
            }
        };
        fetchAllStudents();
    }, [platformUrl, courseIdParam, token, isInstructor]);

    /** -------- Instructors: load all completed dates -------- */
    useEffect(() => {
        const loadAllDates = async () => {
            if (!backEndUrl || !courseIdParam || !resourceId) return;
            if (!isInstructor) return;
            try {
                const url = `${trimBase(backEndUrl)}/external-assignments?courseId=${encodeURIComponent(
                    courseIdParam
                )}&resourceId=${encodeURIComponent(resourceId)}&all=true`;
                const res = await axios.get(url, { headers: tenantHeaders() });
                const items = res?.data?.items || [];
                const map = {};
                for (const it of items) {
                    const uid = it?.UserId || it?.userId;
                    const cd = it?.PlannedDate || it?.plannedDate;
                    if (uid) map[uid] = cd || "";
                }
                setCompletedDatesByUser(map);
            } catch {
                // don't block roster if dates fail
            }
        };
        loadAllDates();
    }, [backEndUrl, courseIdParam, resourceId, isInstructor, tenantId]);

    /** -------- Discover gradebook column from resourceId (hidden from UI) -------- */
    useEffect(() => {
        const discoverColumn = async () => {
            if (!isInstructor) return;
            if (!apiBase() || !token || !courseIdParam || !resourceId) return;
            setColumnError("");
            setLoadingColumn(true);
            try {
                // Optional: verify content exists (ignore error if not accessible)
                const contentUrl = `${apiBase()}/learn/api/public/v1/${coursePath(courseIdParam)}/contents/${encodeURIComponent(resourceId)}`;
                try { await axios.get(contentUrl, { headers: authHeader }); } catch {}

                // Find gradebook column whose contentId === resourceId.
                const tryFind = async (version = "v2") => {
                    const base = `${apiBase()}/learn/api/public/${version}/${coursePath(courseIdParam)}/gradebook/columns`;
                    let url = `${base}?limit=200`;
                    let found = "";
                    while (url && !found) {
                        const res = await axios.get(url, { headers: authHeader });
                        const cols = res?.data?.results || res?.data || [];
                        for (const c of cols) {
                            const cid = c?.id || "";
                            const cContentId = c?.contentId || "";
                            if (cContentId && String(cContentId) === String(resourceId)) { found = cid; break; }
                        }
                        const next = res?.data?.paging?.nextPage;
                        url = !found && next ? `${apiBase()}${next.startsWith("/") ? "" : "/"}${next}` : "";
                    }
                    return found;
                };

                let col = await tryFind("v2");
                if (!col) col = await tryFind("v1");
                if (!col) throw new Error("No gradebook column linked to this content.");

                setColumnId(col);
            } catch (e) {
                setColumnError(e?.response?.data?.message || e?.message || "Could not find a gradebook column for this item.");
            } finally {
                setLoadingColumn(false);
            }
        };
        discoverColumn();
    }, [isInstructor, platformUrl, token, courseIdParam, resourceId]);

    /** -------- Instructors: load existing scores (bulk), map PK -> UUID -------- */
    useEffect(() => {
        const loadExistingScores = async () => {
            if (!isInstructor) return;
            if (!apiBase() || !token || !courseIdParam || !columnId || students.length === 0) return;

            const pkToUuid = new Map(students.map(s => [s.idPk || "", s.uuid || ""]));

            try {
                let url = `${apiBase()}/learn/api/public/v1/${coursePath(courseIdParam)}/gradebook/columns/${encodeURIComponent(columnId)}/users?limit=200`;
                const map = {};

                while (url) {
                    const res = await axios.get(url, { headers: authHeader });
                    const rows = res?.data?.results ?? [];
                    for (const r of rows) {
                        const userPk = r?.userId;
                        const sc = typeof r?.score === "number" ? r.score : null;
                        if (!userPk || sc == null) continue;
                        const uuid = pkToUuid.get(userPk);
                        if (uuid) map[uuid] = sc;
                    }
                    const next = res?.data?.paging?.nextPage;
                    url = next ? `${apiBase()}${next.startsWith("/") ? "" : "/"}${next}` : "";
                }

                setExistingScores(map);

                // Prefill inputs where untouched
                setGrades((prev) => {
                    const next = { ...prev };
                    for (const [uuid, sc] of Object.entries(map)) {
                        if (next[uuid] == null || next[uuid] === "") next[uuid] = sc;
                    }
                    return next;
                });

                // Mark as posted where a score exists
                setRowStatuses((prev) => {
                    const next = { ...prev };
                    for (const uuid of Object.keys(map)) {
                        next[uuid] = { status: "ok", message: "Posted" };
                    }
                    return next;
                });
            } catch {
                // optional: add per-user fallback if needed
            }
        };
        loadExistingScores();
    }, [isInstructor, platformUrl, token, courseIdParam, columnId, students]);

    /** -------- Student: save completed date (TENANT AWARE) -------- */
    const saveCompletedDate = async () => {
        setDateMessage("");
        setError("");
        if (!completedDate) {
            setDateMessage("Please select a date.");
            return;
        }
        if (!backEndUrl) {
            setDateMessage("Missing backEndUrl.");
            return;
        }
        if (!tenantId) {
            setDateMessage("Tenant id required. Provide tenantId prop, ?tenantId=..., or localStorage('tenantId').");
            return;
        }
        if (!resourceId) {
            setDateMessage("Missing resourceId.");
            return;
        }
        try {
            setSavingDate(true);
            const self = selfUuid || (await resolveSelfUuid());
            const payload = {
                courseId: courseIdParam,
                userId: self,
                plannedDate: completedDate,
                resourceId,
            };
            await axios.post(`${trimBase(backEndUrl)}/external-assignments`, payload, {
                headers: tenantHeaders({ "Content-Type": "application/json" }),
            });
            setDateMessage("Your completed date has been saved.");
        } catch (e) {
            setDateMessage(e?.response?.data?.message || e?.message || "Failed to save your date.");
        } finally {
            setSavingDate(false);
        }
    };

    /** -------- Instructors: post grades (backend bridge, TENANT AWARE) -------- */
    const postFilledGrades = async () => {
        if (!lineItemUrl || !backEndUrl) {
            setError("Missing lineItemUrl or backEndUrl.");
            return;
        }
        if (!tenantId) {
            setError("Tenant id required. Provide tenantId prop, ?tenantId=..., or localStorage('tenantId').");
            return;
        }
        setError("");
        setPostNotice(null);

        const entries = Object.entries(grades)
            .filter(([, val]) => val !== "" && val !== undefined && val !== null)
            .map(([uuid, val]) => ({ uuid, score: Number(val) }));

        if (entries.length === 0) {
            setError("No grades entered.");
            return;
        }
        const invalid = entries.find((e) => Number.isNaN(e.score) || e.score < 0 || e.score > 100);
        if (invalid) {
            setError("All entered grades must be numbers between 0 and 100.");
            return;
        }

        setPosting(true);
        setRowStatuses((prev) => ({ ...prev })); // keep existing statuses
        try {
            const results = await Promise.allSettled(
                entries.map(({ uuid, score }) =>
                    axios.post(
                        `${trimBase(backEndUrl)}/lti/grades`,
                        { url: lineItemUrl, userId: uuid, courseId: courseIdParam, score },
                        { headers: tenantHeaders({ "Content-Type": "application/json" }) }
                    )
                )
            );

            let successCount = 0;
            let failCount = 0;
            const newStatuses = {};
            const updatedScores = {};

            entries.forEach(({ uuid, score }, i) => {
                const r = results[i];
                if (r.status === "fulfilled") {
                    successCount += 1;
                    newStatuses[uuid] = { status: "ok", message: "Posted" };
                    updatedScores[uuid] = score; // reflect immediately
                } else {
                    failCount += 1;
                    const err = r.reason;
                    newStatuses[uuid] = { status: "error", message: err?.message || "Failed" };
                }
            });

            if (Object.keys(updatedScores).length) {
                setExistingScores((prev) => ({ ...prev, ...updatedScores }));
            }
            setRowStatuses((prev) => ({ ...prev, ...newStatuses }));

            // Overall notice
            if (successCount > 0 && failCount === 0) {
                setPostNotice({ type: "success", text: `Posted ${successCount} grade${successCount === 1 ? "" : "s"} successfully.` });
            } else if (successCount > 0 && failCount > 0) {
                setPostNotice({ type: "warning", text: `Partial success: ${successCount} posted, ${failCount} failed.` });
            } else {
                setPostNotice({ type: "error", text: "Failed to post all grades." });
            }
        } catch (e) {
            setPostNotice({ type: "error", text: e?.message || "Failed to post one or more grades." });
        } finally {
            setPosting(false);
        }
    };

    const bannerStyles = (type) => {
        const base = { padding: 12, borderRadius: 8, marginTop: 8, fontSize: 14 };
        if (type === "success") return { ...base, background: "#ecfdf5", border: "1px solid #10b981" };
        if (type === "warning") return { ...base, background: "#fffbeb", border: "1px solid #f59e0b" };
        if (type === "error") return { ...base, background: "#fef2f2", border: "1px solid #ef4444" };
        return base;
    };

    return (
        <div style={{ textAlign: "center", marginTop: 40 }}>
            <div
                style={{
                    margin: "12px auto 0",
                    padding: 12,
                    maxWidth: 1000,
                    textAlign: "left",
                    background: "#f6f8fa",
                    borderRadius: 8,
                    fontSize: 14,
                }}
            >
                {/* Minimal header; no role or column id */}
                {!resourceId && (
                    <div style={{ marginTop: 6, color: "#b00020" }}>
                        <strong>Config:</strong> Missing <code>resourceId</code> prop.
                    </div>
                )}
                {backEndUrl && !tenantId && (
                    <div style={{ marginTop: 6, color: "#b45309" }}>
                        <strong>Notice:</strong> Backend calls require a tenant id (<code>{TENANT_HEADER}</code> header).
                    </div>
                )}
                {enrollmentError && (
                    <div style={{ marginTop: 6, color: "#b00020" }}>
                        <strong>Enrollment Error:</strong> {enrollmentError}
                    </div>
                )}
                {columnError && isInstructor && (
                    <div style={{ marginTop: 6, color: "#b00020" }}>
                        <strong>Gradebook:</strong> {columnError}
                    </div>
                )}
                {postNotice && <div style={bannerStyles(postNotice.type)}>{postNotice.text}</div>}
                {error && (
                    <div style={{ marginTop: 6, color: "#b00020" }}>
                        <strong>Error:</strong> {error}
                    </div>
                )}
            </div>

            {/* STUDENT VIEW */}
            {isStudent && (
                <div style={{ marginTop: 24 }}>
                    <p>Please select the date when you completed this assignment.</p>
                    <div
                        style={{
                            margin: "16px auto",
                            padding: 16,
                            maxWidth: 500,
                            textAlign: "left",
                            borderRadius: 8,
                            border: "1px solid #e5e7eb",
                            background: "#fff",
                        }}
                    >
                        <label htmlFor="completedDate" style={{ display: "block", fontWeight: 600, marginBottom: 6 }}>
                            Completed date
                        </label>
                        <input
                            id="completedDate"
                            type="date"
                            value={completedDate}
                            onChange={(e) => setCompletedDate(e.target.value)}
                            style={{
                                padding: "8px 10px",
                                borderRadius: 6,
                                border: "1px solid #ccc",
                                width: "100%",
                                maxWidth: 260,
                            }}
                        />
                        <div style={{ marginTop: 12 }}>
                            <button
                                onClick={saveCompletedDate}
                                disabled={savingDate || !resourceId}
                                style={{
                                    padding: "10px 16px",
                                    fontSize: 16,
                                    borderRadius: 8,
                                    cursor: savingDate ? "not-allowed" : "pointer",
                                }}
                            >
                                {savingDate ? "Saving…" : "Save Date"}
                            </button>
                        </div>
                        {dateMessage && (
                            <div style={{ marginTop: 10, color: dateMessage.startsWith("Failed") ? "#b00020" : "green" }}>
                                {dateMessage}
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* INSTRUCTOR VIEW */}
            {isInstructor && (
                <div style={{ marginTop: 24, padding: "0 16px" }}>
                    <div style={{ textAlign: "left", margin: "0 auto", maxWidth: 1000 }}>
                        <h3>Students</h3>
                        {loadingStudents && <p>Loading students…</p>}
                        {studentsError && (
                            <p style={{ color: "#b00020" }}>
                                <strong>Error:</strong> {studentsError}
                            </p>
                        )}
                        {!loadingStudents && !studentsError && students.length === 0 && <p>No student enrollments found.</p>}
                        {students.length > 0 && (
                            <>
                                <div style={{ overflowX: "auto" }}>
                                    <table style={{ width: "100%", borderCollapse: "collapse", marginTop: 8 }}>
                                        <thead>
                                        <tr>
                                            <th style={{ textAlign: "left", padding: 8, borderBottom: "1px solid #ddd" }}>Name</th>
                                            <th style={{ textAlign: "left", padding: 8, borderBottom: "1px solid #ddd" }}>Completed Date</th>
                                            <th style={{ textAlign: "left", padding: 8, borderBottom: "1px solid #ddd" }}>Existing Score</th>
                                            <th style={{ textAlign: "left", padding: 8, borderBottom: "1px solid #ddd" }}>Score (0–100)</th>
                                            <th style={{ textAlign: "left", padding: 8, borderBottom: "1px solid #ddd" }}>Status</th>
                                        </tr>
                                        </thead>
                                        <tbody>
                                        {students.map((s) => (
                                            <tr key={s.uuid || s.idPk}>
                                                <td style={{ padding: 8, borderBottom: "1px solid #f0f0f0" }}>{s.name}</td>
                                                <td style={{ padding: 8, borderBottom: "1px solid #f0f0f0" }}>
                                                    {completedDatesByUser[s.uuid] || <span style={{ color: "#888" }}>—</span>}
                                                </td>
                                                <td style={{ padding: 8, borderBottom: "1px solid #f0f0f0" }}>
                                                    {existingScores[s.uuid] != null ? (
                                                        <span>{existingScores[s.uuid]}</span>
                                                    ) : (
                                                        <span style={{ color: "#888" }}>—</span>
                                                    )}
                                                </td>
                                                <td style={{ padding: 8, borderBottom: "1px solid #f0f0f0" }}>
                                                    <input
                                                        type="number"
                                                        min={0}
                                                        max={100}
                                                        step="1"
                                                        value={grades[s.uuid] ?? ""}
                                                        onChange={(e) => {
                                                            const v = e.target.value;
                                                            setGrades((prev) => ({ ...prev, [s.uuid]: v }));
                                                            setRowStatuses((prev) => ({ ...prev, [s.uuid]: undefined }));
                                                        }}
                                                        placeholder="—"
                                                        style={{
                                                            width: 120,
                                                            padding: "6px 8px",
                                                            borderRadius: 6,
                                                            border: "1px solid #ccc",
                                                        }}
                                                        disabled={posting}
                                                    />
                                                </td>
                                                <td style={{ padding: 8, borderBottom: "1px solid #f0f0f0" }}>
                                                    {rowStatuses[s.uuid]?.status === "ok" && <span style={{ color: "green" }}>Posted</span>}
                                                    {rowStatuses[s.uuid]?.status === "error" && (
                                                        <span style={{ color: "#b00020" }}>{rowStatuses[s.uuid]?.message}</span>
                                                    )}
                                                    {!rowStatuses[s.uuid] && <span style={{ color: "#888" }}>—</span>}
                                                </td>
                                            </tr>
                                        ))}
                                        </tbody>
                                    </table>
                                </div>
                                <div style={{ textAlign: "right", marginTop: 12 }}>
                                    <button
                                        onClick={postFilledGrades}
                                        disabled={posting}
                                        style={{
                                            padding: "10px 16px",
                                            fontSize: 16,
                                            borderRadius: 8,
                                            cursor: posting ? "not-allowed" : "pointer",
                                        }}
                                    >
                                        {posting ? "Posting…" : "Post Selected Grades"}
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}

            {!isStudent && !isInstructor && (
                <p style={{ marginTop: 16 }}>
                    This tool is available to Students (to set a completed date) and Instructors (to view dates and post grades).
                </p>
            )}
        </div>
    );
};

export default ExternalAssignment;
